import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { ReviewBadge } from "@/profitability/overview/period-review";
import { ShareSummary } from "@/profitability/overview/share-summary";
import type { RecordObject } from "@/profitability/records/objects";
import type { TemplateSetting } from "@/profitability/settings/types";
import type { ProfitabilityReport } from "@/profitability/types";
import { PeriodContext } from "./period-context";
import { PeriodPicker } from "./period-picker";
import { usePeriodShare } from "./period-share";
import { periodResultId } from "./run-select";
import { TemplateActions } from "./template-actions";
import type { TemplatePeriod } from "./use-template-period";

/**
 * The period on every page of the agent except Review, which keeps it in the
 * findings column. Its name is the page title, the grouped control sits
 * beside that title on the left of the row, and one quiet line under it says
 * what the week is compared with. Ask, share, and the agent menu stay on the
 * right. A period someone signed off, or one revised after that, keeps that
 * badge beside the title. Open findings are the Review count. Before the
 * agent has a period, the agent's name is the title.
 */
export const TemplateHeader = ({
  businessId,
  report,
  period,
  objects,
  settings,
  compare,
  onCompareChange,
  onPeriodChange,
  onAllPeriods,
  onAsk,
  publish,
}: {
  businessId: string;
  report: ProfitabilityReport;
  period: TemplatePeriod;
  objects: RecordObject[];
  /** The settings the period's run read. */
  settings: TemplateSetting[] | undefined;
  compare: string | null;
  onCompareChange: (compare: string | null) => void;
  onPeriodChange: (runId: string | null) => void;
  onAllPeriods: () => void;
  /** Opens the chat, asking `question` when there is one. */
  onAsk: (question: string | null) => void;
  /** Publish, while the agent is being set up. */
  publish?: ReactNode;
}) => {
  const { section } = useParams();
  const { overview, run, review, label, periodChanging, requestedId } = period;
  const share = usePeriodShare(report.name, period, objects);
  const periods = overview.data?.periods ?? [];
  const titlePending =
    (overview.isPending && !run) || (periodChanging && !label);
  const contextPending =
    periodChanging || (overview.isPending && !overview.data);
  const actionsPending = overview.isPending && !run;
  const showMarks = !!run && !periodChanging;
  // Review's page is the finding. Its column carries the period. Upload,
  // share, and the agent menu stay on this header, on the other pages.
  if (section === "review") return null;
  return (
    // The title is the period. A narrow page wraps the actions under it.
    <Stack
      direction="row"
      align="start"
      justify="between"
      gap={4}
      className="w-full flex-wrap gap-y-3 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
    >
      <Stack align="start" gap={1} className="min-w-0 flex-1 basis-96">
        <Stack
          direction="row"
          align="center"
          gap={2}
          className="min-w-0 flex-wrap gap-x-3"
        >
          {titlePending ? (
            <Skeleton className="h-10 w-72 max-w-full" />
          ) : (
            <PageTitle className="min-w-0 [overflow-wrap:anywhere] tabular-nums">
              {run ? (label ?? report.name) : report.name}
            </PageTitle>
          )}
          {showMarks && <ReviewBadge review={review} />}
          {actionsPending ? (
            <Skeleton className="h-9 w-36 shrink-0" aria-hidden />
          ) : (
            run && (
              <PeriodPicker
                periods={periods}
                current={
                  periodChanging && requestedId
                    ? requestedId
                    : periodResultId(overview.data?.runs, run.id)
                }
                onChange={onPeriodChange}
                onAllPeriods={onAllPeriods}
              />
            )
          )}
        </Stack>
        {(run || overview.isPending) &&
          (contextPending ? (
            <Skeleton className="h-5 w-64 max-w-full" />
          ) : (
            run &&
            overview.data && (
              <PeriodContext
                overview={overview.data}
                run={run}
                settings={settings}
                compare={compare}
                onCompareChange={onCompareChange}
                onRunChange={onPeriodChange}
              />
            )
          ))}
      </Stack>
      <Stack
        direction="row"
        align="center"
        justify="end"
        gap={2}
        className="flex-wrap pt-2 max-sm:justify-start"
      >
        {actionsPending ? (
          <>
            <Skeleton className="h-9 w-28" aria-hidden />
            <Skeleton className="h-9 w-24" aria-hidden />
          </>
        ) : (
          run && (
            <>
              <Button variant="secondary" onClick={() => onAsk(null)}>
                <ChatCircleIcon data-icon="inline-start" />
                Ask Volca
              </Button>
              <ShareSummary
                subject={share.subject}
                text={share.text}
                onSummarize={() => onAsk(`Summarize ${label ?? "this period"}`)}
              />
            </>
          )
        )}
        <TemplateActions
          businessId={businessId}
          report={report}
          run={run}
          review={review}
          reviewLabel={overview.data?.review?.labels.reviewed}
          publish={publish}
        />
      </Stack>
    </Stack>
  );
};
